/*
  AMORELLE JEWELRY — Required option gate ("Ring Size")
  =====================================================
  Used ONLY by templates that keep Shopify's native product form (the default
  product template — "Schema B": Center Stone / Ring Size). Templates driven by
  assets/amorelle-purchase-controller.js do their own gating and do not render
  this snippet.

  Keeping the native form on that template is deliberate: it preserves
  Shopify's own cart, drawer, sticky-bar and analytics behaviour untouched.
  The gate only withholds the submit controls until the shopper actively
  chooses a ring size, because Shopify always resolves a variant and a
  never-touched size control would otherwise ship size 4.

  ---------------------------------------------------------------------------
  BUG FIXED IN THIS REVISION — the gate permanently disabled Add to Bag
  ---------------------------------------------------------------------------
  applyLock() recorded "what the theme wanted" so unlock() could restore it:

      if (typeof btn.__amThemeDisabled === 'undefined') {
        btn.__amThemeDisabled = btn.disabled === true;
      }
      btn.disabled = true;

  and watch() re-asserted the lock whenever the theme re-enabled the button,
  guarded by `if (!self.locked || self.applying) return;`.

  That guard never worked. MutationObserver callbacks are delivered as a
  MICROTASK after the current task finishes, but `applying` was reset to false
  synchronously on the last line of applyLock(). So the observer went on to
  process the gate's OWN write, executed
  `target.__amThemeDisabled = target.disabled === true`, and recorded TRUE.
  unlock() then faithfully restored that poisoned value and re-disabled the
  button at the exact moment it should have released it. refresh() bails when
  `locked` is false, so nothing ever recovered: Add to Bag stayed dead for the
  life of the page.

  THE FIX, in three parts:
    1. observer.takeRecords() is drained immediately after our own writes, so
       the gate's mutations are discarded before the callback can ever see
       them. This is the actual repair.
    2. `applying` is cleared in a microtask rather than synchronously, as a
       second line of defence for records queued by other means.
    3. unlock() no longer trusts a remembered flag alone. It re-derives
       availability from the theme's own hidden variant select, so a genuinely
       sold-out variant stays disabled and everything else is released.
*/

(function () {
  'use strict';

  var GATE_ATTR = 'data-am-required-option-gate';
  var GATE_SELECTOR = '[' + GATE_ATTR + ']';
  var PROMPT_SELECTOR = '[data-am-required-option-prompt]';
  var BUTTON_SELECTOR = '[data-add-to-cart]';
  var PAYMENT_SELECTOR = '.shopify-payment-button';
  var LOCKED_CLASS = 'am-option-gate--locked';
  var STYLE_ID = 'am-required-option-gate-styles';
  var SCOPE_SELECTOR = '.quick-product, .product__top, [data-section-id], .shopify-section';
  var RELEASE_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', ' ', 'Spacebar', 'Home', 'End'];

  var CSS = [
    '.am-option-gate{margin:0 0 12px;}',
    '.am-option-gate__prompt{display:block;margin:0;padding:8px 12px;border:1px solid;border-radius:2px;opacity:.85;}',
    '.am-option-gate__prompt[hidden]{display:none;}',
    '.am-option-gate__prompt.is-emphasised{opacity:1;}',
    '.' + LOCKED_CLASS + ' ' + PAYMENT_SELECTOR + '{opacity:.35;pointer-events:none;}',
    '.' + LOCKED_CLASS + ' ' + BUTTON_SELECTOR + '{cursor:not-allowed;}'
  ].join('');

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.appendChild(document.createTextNode(CSS));
    (document.head || document.documentElement).appendChild(style);
  }

  function microtask(fn) {
    if (typeof queueMicrotask === 'function') queueMicrotask(fn);
    else Promise.resolve().then(fn);
  }

  function normalise(value) {
    return String(value == null ? '' : value).replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function findOptionControl(scope, optionName) {
    var selects = scope.querySelectorAll('select[name^="options["]');
    for (var i = 0; i < selects.length; i++) {
      var raw = selects[i].getAttribute('name') || '';
      var match = raw.match(/^options\[([\s\S]*)\]$/);
      if (match && normalise(match[1]) === optionName) return selects[i];
    }
    return null;
  }

  function Gate(el) {
    this.el = el;
    this.optionName = normalise(el.getAttribute(GATE_ATTR)) || 'ring size';
    this.prompt = el.querySelector(PROMPT_SELECTOR);
    this.form = el.closest('form');
    this.scope = (this.form && this.form.closest(SCOPE_SELECTOR)) || el.closest(SCOPE_SELECTOR) || document;
    this.select = findOptionControl(this.scope, this.optionName);
    this.optionWrap = this.select ? this.select.closest('[data-product-option]') : null;
    this.buttons = [];
    this.locked = false;
    this.applying = false;
    this.observer = null;
  }

  Gate.prototype.canGate = function () {
    return !!(this.form && this.select && this.select.options && this.select.options.length > 1);
  };

  Gate.prototype.hasChoice = function () {
    return !!(this.select && String(this.select.value || '').trim() !== '');
  };

  /* A ?variant= link is an explicit variant choice made by the shopper (or by
     a Shopping ad they clicked). Gating it would contradict the URL and would
     hide the advertised price behind an extra click. */
  Gate.prototype.urlPreselected = function () {
    try {
      return new URLSearchParams(window.location.search).has('variant');
    } catch (e) {
      return false;
    }
  };

  Gate.prototype.collectButtons = function () {
    this.buttons = Array.prototype.slice.call(this.form.querySelectorAll(BUTTON_SELECTOR));
    return this.buttons;
  };

  /* Re-derive availability from the theme's own hidden variant select instead
     of trusting a remembered flag. If the currently selected option is marked
     unavailable by the theme, the button stays disabled. */
  Gate.prototype.variantUnavailable = function () {
    var idSelect = this.form.querySelector('select[name="id"], [data-variant-select]');
    if (!idSelect || !idSelect.options || idSelect.selectedIndex < 0) return false;
    var opt = idSelect.options[idSelect.selectedIndex];
    if (!opt) return false;
    if (opt.disabled) return true;
    return /sold out|unavailable/i.test(opt.textContent || '');
  };

  Gate.prototype.applyLock = function () {
    var self = this;
    this.applying = true;
    this.form.classList.add(LOCKED_CLASS);

    this.buttons.forEach(function (btn) {
      if (typeof btn.__amThemeDisabled === 'undefined') {
        btn.__amThemeDisabled = btn.disabled === true;
      }
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      if (self.prompt && self.prompt.id) {
        btn.setAttribute('aria-describedby', self.prompt.id);
      }
    });

    if (this.prompt) this.prompt.hidden = false;

    /* FIX 1 — discard the records our own writes just queued, so the observer
       callback can never mistake them for the theme's intent. */
    if (this.observer) this.observer.takeRecords();

    /* FIX 2 — clear the guard in a microtask, after the observer would have
       been delivered, rather than synchronously. */
    microtask(function () { self.applying = false; });
  };

  Gate.prototype.lock = function () {
    if (this.locked) return;
    this.locked = true;
    this.collectButtons();
    this.watch();
    this.applyLock();
  };

  Gate.prototype.unlock = function () {
    if (!this.locked) return;
    this.locked = false;

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.form.classList.remove(LOCKED_CLASS);

    /* FIX 3 — the live variant wins over the remembered flag. */
    var unavailable = this.variantUnavailable();

    this.buttons.forEach(function (btn) {
      try { delete btn.__amThemeDisabled; } catch (e) { btn.__amThemeDisabled = undefined; }
      btn.disabled = unavailable;
      if (unavailable) {
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.removeAttribute('aria-disabled');
      }
      btn.removeAttribute('aria-describedby');
    });

    if (this.prompt) {
      this.prompt.hidden = true;
      this.prompt.classList.remove('is-emphasised');
    }
  };

  Gate.prototype.watch = function () {
    var self = this;
    if (!window.MutationObserver || this.observer) return;

    this.observer = new MutationObserver(function (mutations) {
      if (!self.locked || self.applying) return;
      var reapply = false;

      mutations.forEach(function (mutation) {
        var target = mutation.target;
        if (mutation.attributeName === 'disabled') {
          target.__amThemeDisabled = target.disabled === true;
          if (!target.disabled) reapply = true;
        } else if (mutation.attributeName === 'aria-disabled') {
          if (target.getAttribute('aria-disabled') !== 'true') reapply = true;
        }
      });

      if (reapply) self.applyLock();
    });

    this.collectButtons().forEach(function (btn) {
      self.observer.observe(btn, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
    });
  };

  Gate.prototype.refresh = function () {
    if (!this.locked || !this.form) return;

    if (this.select && !this.select.isConnected) {
      this.select = findOptionControl(this.scope, this.optionName);
      this.optionWrap = this.select ? this.select.closest('[data-product-option]') : null;
      if (this.select) this.bind();
    }

    var previous = this.buttons;
    var current = this.collectButtons();
    var changed = current.length !== previous.length;

    if (!changed) {
      for (var i = 0; i < current.length; i++) {
        if (current[i] !== previous[i]) { changed = true; break; }
      }
    }
    if (!changed) return;

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.watch();
    this.applyLock();
  };

  Gate.prototype.announce = function () {
    if (!this.prompt) return;
    var self = this;
    var text = this.prompt.textContent;
    this.prompt.classList.add('is-emphasised');
    this.prompt.textContent = '';
    window.setTimeout(function () { self.prompt.textContent = text; }, 60);
  };

  Gate.prototype.focusOption = function () {
    if (!this.select) return;
    var target = this.select;
    if (this.optionWrap) target = this.optionWrap.querySelector('[data-button]') || this.select;
    try { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
    try { target.focus({ preventScroll: true }); } catch (e) {
      try { target.focus(); } catch (e2) {}
    }
  };

  Gate.prototype.bind = function () {
    var self = this;
    if (!this.select || this.select.__amGateBound) return;
    this.select.__amGateBound = true;

    var release = function (event) {
      if (event && event.isTrusted === false) return;
      if (!self.hasChoice()) return;
      self.unlock();
    };

    this.select.addEventListener('change', function (event) {
      if (event && event.isTrusted === false) return;
      if (!self.hasChoice()) { self.lock(); return; }
      self.unlock();
    });

    this.select.addEventListener('click', release);
    this.select.addEventListener('keydown', function (event) {
      if (RELEASE_KEYS.indexOf(event.key) !== -1) release(event);
    });

    if (this.optionWrap) {
      this.optionWrap.addEventListener('click', function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        if (!target.closest('[data-option-buttons], .product__color-swatches, .product__color-chips')) return;
        if (target.closest('button, label, input, [data-button], [data-option-value]')) release(event);
      });
    }
  };

  Gate.prototype.guard = function () {
    var self = this;
    if (!this.form || this.form.__amGateGuarded) return;
    this.form.__amGateGuarded = true;

    this.form.addEventListener('click', function (event) {
      if (!self.locked) return;
      var target = event.target;
      if (!target || !target.closest) return;
      if (!target.closest(BUTTON_SELECTOR + ', ' + PAYMENT_SELECTOR)) return;
      event.preventDefault();
      event.stopPropagation();
      self.announce();
      self.focusOption();
    }, true);

    this.form.addEventListener('submit', function (event) {
      if (!self.locked) return;
      event.preventDefault();
      event.stopPropagation();
      self.announce();
      self.focusOption();
    }, true);
  };

  function initGate(el) {
    if (el.__amGate) { el.__amGate.refresh(); return; }

    var gate = new Gate(el);
    if (!gate.form) return;

    if (!gate.select) {
      el.__amGateAttempts = (el.__amGateAttempts || 0) + 1;
      if (el.__amGateAttempts === 10 && window.console && window.console.warn) {
        window.console.warn('[Amorelle] Required-option gate: no "' + gate.optionName + '" control found near this product form. Add to Cart left enabled.');
      }
      return;
    }

    el.__amGate = gate;
    if (!gate.canGate()) return;

    injectStyles();
    gate.guard();
    gate.bind();

    /* A ?variant= link states the size explicitly — honour it. */
    if (gate.urlPreselected() && gate.hasChoice()) return;

    gate.lock();
  }

  function initAll() {
    var gates = document.querySelectorAll(GATE_SELECTOR);
    for (var i = 0; i < gates.length; i++) initGate(gates[i]);
  }

  function boot() {
    initAll();
    if (window.MutationObserver && document.body) {
      var pending = false;
      var observer = new MutationObserver(function () {
        if (pending) return;
        pending = true;
        var run = function () { pending = false; initAll(); };
        if (window.requestAnimationFrame) window.requestAnimationFrame(run);
        else window.setTimeout(run, 16);
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('quickview:loaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
})();
